import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useTranslation } from 'react-i18next';
import { useCreateMarket, useUpdateMarket } from '@/hooks/usePartnerships';
import type { Market } from '../../../../shared/types/partnerships';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  market?: Market;
}

interface FormData {
  name: string;
  countryCode: string;
  region: string;
  currency: string;
  notes: string;
}

export function MarketFormDialog({ open, onOpenChange, market }: Props) {
  const { t } = useTranslation();
  const isEditing = !!market;
  const create = useCreateMarket();
  const update = useUpdateMarket();
  const [active, setActive] = useState(true);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>();

  useEffect(() => {
    if (open) {
      setActive(market?.active ?? true);
      reset({
        name: market?.name || '',
        countryCode: market?.countryCode || '',
        region: market?.region || '',
        currency: market?.currency || '',
        notes: market?.notes || '',
      });
    }
  }, [open, market, reset]);

  const onSubmit = async (data: FormData) => {
    if (isEditing) {
      await update.mutateAsync({
        marketId: market!.marketId,
        name: data.name,
        countryCode: data.countryCode || undefined,
        region: data.region || undefined,
        currency: data.currency || undefined,
        notes: data.notes || undefined,
        active,
      });
    } else {
      await create.mutateAsync({
        name: data.name,
        countryCode: data.countryCode,
        region: data.region || undefined,
        currency: data.currency || undefined,
        notes: data.notes || undefined,
      });
    }
    onOpenChange(false);
  };

  const isBusy = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? t('partnerships.market_form.title_edit') : t('partnerships.market_form.title_create')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('partnerships.common.name_required')}</Label>
              <Input {...register('name', { required: true })} placeholder="Argentina" />
              {errors.name && <p className="text-xs text-destructive">{t('partnerships.common.required_field')}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>{t('partnerships.market_form.country_code_label')}</Label>
              <Input
                {...register('countryCode', { required: !isEditing })}
                placeholder="AR"
                maxLength={3}
              />
              {errors.countryCode && <p className="text-xs text-destructive">{t('partnerships.common.required_field')}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('partnerships.market_form.region_label')}</Label>
              <Input {...register('region')} placeholder="LATAM" />
            </div>
            <div className="space-y-1.5">
              <Label>{t('partnerships.market_form.currency_label')}</Label>
              <Input {...register('currency')} placeholder={t('partnerships.market_form.ars_usd')} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.market_form.notes_label')}</Label>
            <Textarea {...register('notes')} rows={2} />
          </div>

          {isEditing && (
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="text-sm font-medium">{t('partnerships.market_form.active_label')}</p>
                <p className="text-xs text-muted-foreground">{t('partnerships.market_form.active_description')}</p>
              </div>
              <Switch checked={active} onCheckedChange={setActive} />
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('common.ui.cancel')}</Button>
            <Button type="submit" disabled={isBusy}>
              {isBusy ? t('partnerships.common.saving') : isEditing ? t('partnerships.common.save_changes') : t('partnerships.market_form.create_button')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
